import type { CloudVault } from './cloudVault';
import { catalogByPath } from '../entity/catalog';
import { isConflictSibling } from '../plan/board';

/**
 * The files that may be of one kind — every arc, every nook — without opening
 * the whole vault. Mirrors the app's `KindPaths`.
 *
 * A path is not what makes a file an arc: its front matter is, and the readers
 * check it. A path only says where to look. A kind's own folder is always
 * looked in, and the date tree and the kind folders never are, since nothing
 * writes an arc or a nook there. A file anywhere else — a folder made into a
 * directory, a file moved by hand — is a candidate too: the catalog rules it
 * out when it says the file is of another kind and still matches it, and
 * anything the catalog does not vouch for is read.
 */
export type UnitKind = 'arc' | 'workspace';

/** Where a new file of each kind is made, and where one is always looked for. */
const HOME: Record<UnitKind, string> = { arc: 'arcs/', workspace: 'spaces/' };

export function homeOf(kind: UnitKind): string {
  return HOME[kind];
}

/** `threads/` is a legacy folder nothing writes into any more, holding
 *  hundreds of files that would otherwise be weighed on every listing. */
const KIND_FOLDERS = ['arcs/', 'spaces/', 'threads/'];

const DATE_TREE = /^\d{4}\//;

export type KindVault = Pick<CloudVault, 'listPaths' | 'readFile' | 'contentHash'>;

/**
 * The kind a path's folder suggests, for a check that has neither the file nor
 * a loaded listing in reach. It cannot see an arc or a nook kept anywhere else.
 */
export function kindByFolder(path: string): UnitKind | null {
  if (path.startsWith(HOME.arc)) return 'arc';
  if (path.startsWith(HOME.workspace)) return 'workspace';
  return null;
}

/** Whether [path] is an arc: one the loaded plan holds, or one in `arcs/`. */
export function isArcIn(path: string, arcs: readonly { path: string }[] | null | undefined): boolean {
  return kindByFolder(path) === 'arc' || (arcs?.some((a) => a.path === path) ?? false);
}

/** Whether [path] is a nook: one of the loaded nooks, or one in `spaces/`. */
export function isNookIn(path: string, nooks: readonly { path: string }[] | null | undefined): boolean {
  return kindByFolder(path) === 'workspace' || (nooks?.some((n) => n.path === path) ?? false);
}

/**
 * Whether a file read as a candidate is of [kind]. One in the kind's own
 * folder is taken as it always has been; anywhere else, only its declared
 * `kind:` counts.
 */
export function isOfKind(kind: UnitKind, path: string, declared: string): boolean {
  return path.startsWith(HOME[kind]) || declared === kind;
}

/** A file the app imports as a unit, as opposed to its machine state. */
function isUnitPath(path: string): boolean {
  if (!path.endsWith('.md') || isConflictSibling(path)) return false;
  if (path.startsWith('verticals/')) return false;
  return !path.split('/').some((segment) => segment.startsWith('.') || segment.startsWith('_'));
}

/** Every path to read for [kind], its own folder first. */
export async function pathsOfKind(vault: KindVault, kind: UnitKind): Promise<string[]> {
  const home = vault.listPaths(HOME[kind]).filter(isUnitPath);
  const elsewhere = vault
    .listPaths('')
    .filter((p) => isUnitPath(p) && !DATE_TREE.test(p) && !KIND_FOLDERS.some((folder) => p.startsWith(folder)));
  if (elsewhere.length === 0) return home;

  const catalog = await catalogByPath(vault);
  const kept = elsewhere.filter((p) => {
    const row = catalog.get(p);
    if (!row || row.kind === kind) return true;
    // A row written before the file last changed may name what it used to be.
    const hash = vault.contentHash(p);
    return hash !== null && row.hash !== hash;
  });
  return [...home, ...kept];
}
